/**
 * Traspaso de la semana (y el recorrido) elegidos, del mapa de la vista de
 * sesión a la pantalla del informe.
 *
 * Existe porque `LayoutService.navigate(route)` solo lleva una ruta: sin esto,
 * ir a `/monitoreo/reporte` desde una fecha aterrizaría en la semana más
 * reciente y "el informe del día" sería mentira.
 *
 * Es deliberadamente lo más chico posible —un valor en memoria, sin servicio ni
 * evento— porque el dato vive lo que dura un `navigate`: lo escribe quien
 * navega y lo consume la página que se monta a continuación.
 */

/** Qué informe pidió ver quien navegó. */
export type SeleccionReporte = {
  /**
   * Fecha de referencia (`yyyy-MM-dd`): la del recorrido elegido, o la de la
   * semana si se pidió completa.
   *
   * Va la fecha y **no el año ISO** porque la pantalla del informe arma su
   * selector de año con `fecha.slice(0, 4)`, o sea el año del calendario. En
   * la semana que cruza diciembre los dos no coinciden —la semana 1 puede
   * empezar el 29 de diciembre— y buscar por año ISO no encontraría nada.
   * Guardando la fecha, quien la consume aplica su propio criterio de
   * agrupación y no hay dos reglas compitiendo.
   */
  fecha: string;
  /** Número de semana ISO, para ubicarla dentro del año. */
  semana: number;
  /** Recorrido puntual, o `null` para la semana completa. */
  sesionId: string | null;
};

/** Selección a la espera de que la pantalla del informe la consuma. */
let _pendiente: SeleccionReporte | null = null;

/** Deja anotado qué informe abrir, justo antes de navegar. */
export function setSeleccionReporte(seleccion: SeleccionReporte): void {
  _pendiente = seleccion;
}

/**
 * Devuelve la selección pendiente y **la consume**.
 *
 * Se limpia al leerla a propósito: si quedara guardada, entrar más tarde a la
 * pantalla del informe por cualquier otro camino —el acceso del dashboard, por
 * ejemplo— reviviría una semana vieja que el usuario no pidió.
 */
export function takeSeleccionReporte(): SeleccionReporte | null {
  const seleccion = _pendiente;
  _pendiente = null;
  return seleccion;
}
